import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import {
  white,
  gray100,
  gray60,
  gray80,
  primaryButtonBackgroundColor,
  gray40,
  white_overlay80,
} from '../../../constants/colors';
import StaffListCard from '../../uiParts/card/StaffListCard';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { getUsersByCompanyId, getUser } from '../../../api/firebase/firestore/user/read';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { getGroups } from '../../../api/firebase/firestore/group/read';
import SideBar from '../../uiParts/menu/SideBar';
import { registerStatus, companyPlan as companyPlanEnum, userRole } from '../../../constants/enum';
import { boldXxl, spHeaderTitle } from '../../../constants/location';
import {
  USER_ROLE_COMPANY_ADMIN,
  USER_ROLE_COMPANY_MEMBER,
  STAFF_LIST_TITLE,
} from '../../../constants/labels';
import MetaTitle from '../../utils/MetaTitle';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { isNotCompanyMember } from '../../../utils/permission';
import { useCompanyId } from '../../../hooks/useCompanyId';
import AccountManagementDialog from '../../uiParts/modal/AccountManagementDialog';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import type { UserType } from '../../../types/user';
import * as Sentry from '@sentry/react';

const styles = {
  page: {
    height: '100%',
    display: 'flex',
    paddingBottom: '236px',
    '@media (max-width: 960px)': {
      flexDirection: 'column',
      paddingTop: '0',
      justifyContent: 'flex-start',
      paddingBottom: '100px',
    },
  },
  pageBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    height: '80px',
    textAlign: 'center',
    backgroundColor: white,
    borderBottom: `1px solid ${gray40}`,
    Padding: '24px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    '@media (max-width: 960px)': {
      height: '41px',
      marginTop: '48px',
      padding: '0 10px 0 16px',
      borderTop: `1px solid ${gray40}`,
    },
  },
  mobileInviteButtonSpace: {
    '@media (max-width: 960px)': {
      borderTop: `1px solid ${gray40}`,
      marginLeft: '0',
    },
  },
  headerTitle: {
    ...boldXxl,
    '@media (max-width: 960px)': {
      ...spHeaderTitle,
    },
  },
  mainContainer: {
    flexDirection: 'column',
    gap: '16px',
    width: 'calc(100% - 64px)',
    boxSizing: 'border-box',
    backgroundColor: white,
    margin: '0px 32px 16px 32px',
    '@media screen and (max-width:960px)': {
      margin: '0',
      width: '100%',
      overflowX: 'auto',
    },
  },
  sidebar: {
    position: 'fixed',
    color: 'white',
    textAlign: 'center',
    backgroundColor: gray100,
    width: '240px',
    height: '100vh',
    flexShrink: 0,
    zIndex: 1000,
    '@media (max-width: 960px)': {
      width: '100%',
      minWidth: '240px',
      height: '48px',
    },
  },
  button: {
    border: 'none',
    fontWeight: 700,
    fontSize: '16px',
    textAlign: 'center',
    width: '192px',
    height: '44px',
    '@media (max-width: 960px)': {
      width: '100%',
    },
  },
  notDisplayMobile: {
    '@media (max-width: 960px)': {
      display: 'none',
    },
  },
  mobileStaffInviteButton: {
    display: 'none',
    '@media (max-width: 960px)': {
      display: 'block',
    },
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  searchContainer: {
    width: 'calc(100% - 64px)',
    margin: '0 32px',
    boxSizing: 'border-box',
    '@media screen and (max-width:960px)': {
      margin: '0 16px',
      width: 'calc(100% - 32px)',
    },
  },
  searchFormBox: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
    padding: 2,
    border: `1px solid ${gray40}`,
    borderRadius: 1,
    backgroundColor: white,
  },
  searchField: {
    minWidth: '420px',
    textAlign: 'left',
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
      borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
    },
    '@media screen and (max-width:960px)': {
      minWidth: '100%',
    },
  },
  selectField: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
    minWidth: '200px',
    textAlign: 'left',
    '@media screen and (max-width:960px)': {
      minWidth: '100%',
    },
  },
  clearButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid ${gray60}`,
      boxShadow: 'none',
      color: gray60,
      opacity: '80%',
    },
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: 'none',
    color: gray80,
    height: '40px',
    marginLeft: 'auto',
  },
};

// ユーザーの型は types/user.ts の UserType に一本化する（#3071）
type User = UserType;

const InviteButton = ({
  forPc,
  registeredUsers,
  userLimit,
  onLimitReached,
}: {
  forPc: boolean;
  registeredUsers: number;
  userLimit: number;
  onLimitReached: () => void;
}): ReactElement => {
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();

  return (
    <Button
      onClick={(): void => {
        if (registeredUsers >= userLimit) {
          onLimitReached();
          return;
        }
        navigate('/staff/invite');
      }}
      fullWidth
      variant="outlined"
      sx={[primaryButtonBackgroundColor, styles.button, forPc ? styles.notDisplayMobile : null]}
    >
      ＋ 担当者を招待する
    </Button>
  );
};

const isAdmin = (user: User): boolean => isNotCompanyMember(user.role);

const Staff: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loginUser, setLoginUser] = useState<User | null>(null);
  const [companyPlan, setCompanyPlan] = useState<number>(0);
  const [userLimit, setUserLimit] = useState<number>(0);
  const [limitDialogOpen, setLimitDialogOpen] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<number | ''>('');
  const [groupNameMap, setGroupNameMap] = useState<Record<string, string>>({});
  const { user, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId } = useCompanyId();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const navigate = useNavigate();

  useEffect(() => {
    // ユーザーがログインしていない場合は何もしない
    if (!user) {
      return;
    }

    const fetchUsersAndCompany = async (): Promise<void> => {
      try {
        let fetchedUsers: User[] = [];

        // 会社のメンバーを取得（代理モード時は対象企業のメンバー）
        const companyIdToUse = effectiveCompanyId || user.company_id;
        if (companyIdToUse) {
          fetchedUsers = (await getUsersByCompanyId(companyIdToUse)) as User[];

          // 所属グループバッジ表示用にグループ名マップを構築（#2804）
          try {
            const groups = await getGroups(companyIdToUse);
            setGroupNameMap(
              groups.reduce<Record<string, string>>((acc, g) => {
                acc[g.id] = g.name;
                return acc;
              }, {}),
            );
          } catch (groupError) {
            // グループ取得失敗はバッジ非表示に留め、担当者一覧の表示は止めない
            Sentry.captureException(groupError);
          }

          // 企業情報を取得
          const companyData = await getCompany(companyIdToUse);
          if (companyData) {
            setCompanyPlan(companyData.plan ?? 0);
            const limit = companyData.user_limit ?? 0;
            setUserLimit(limit);

            // userLimitが取得できなかった場合のエラー表示
            if (limit === 0) {
              setSnackbarMessage(
                '企業情報の読み込みに失敗しました。ページを再読み込みしてください。',
              );
              setSnackbarOpen(true);
            }
          }
        }

        setUsers(fetchedUsers);
      } catch (error: unknown) {
        // Sentryにエラーを送信
        Sentry.captureException(error);
        // ユーザーにエラーメッセージを表示
        setSnackbarMessage('担当者データの取得に失敗しました。');
        setSnackbarOpen(true);
      }
    };

    void fetchUsersAndCompany();
  }, [user, effectiveCompanyId]);

  useEffect(() => {
    if (user !== null) {
      const fetchLoginUser = async (): Promise<void> => {
        const loginUser = await getUser(user?.uid);
        // ユーザー情報の取得に失敗した場合は読取パターンページへリダイレクト
        if (!loginUser) {
          navigate('/pattern');
        }
        setLoginUser(loginUser as User);
      };

      fetchLoginUser();
    }
  }, [user]);

  const filteredUsers = useMemo(() => {
    let result = users;

    if (searchText) {
      const keyword = searchText.toLowerCase();
      result = result.filter(
        u =>
          (u.name ?? '').toLowerCase().includes(keyword) ||
          u.email.toLowerCase().includes(keyword) ||
          (u.department ?? '').toLowerCase().includes(keyword),
      );
    }

    if (roleFilter !== '') {
      result = result.filter(u => u.role === roleFilter);
    }

    return result;
  }, [users, searchText, roleFilter]);

  if (loginUser === null || isLoading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  // testLoginUserがnullでない場合のみisAdminを呼び出す
  const isUserAdmin = loginUser ? isAdmin(loginUser) : false;

  const registeredUsers = users.filter(
    user =>
      user.register_status === registerStatus.finished ||
      user.register_status === registerStatus.emailRegistered,
  ).length;

  const handleLimitReached = (): void => {
    setLimitDialogOpen(true);
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            gap: '16px',
          },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Typography sx={styles.headerTitle}>{STAFF_LIST_TITLE}</Typography>
          {isUserAdmin ? (
            <InviteButton
              forPc={true}
              registeredUsers={registeredUsers}
              userLimit={userLimit}
              onLimitReached={handleLimitReached}
            />
          ) : null}
        </Box>
        <Box sx={styles.searchContainer}>
          <Box sx={styles.searchFormBox}>
            <TextField
              value={searchText}
              onChange={(e): void => setSearchText(e.target.value)}
              placeholder="担当者名・部署名・メールアドレスで検索"
              sx={styles.searchField}
              size="small"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Select
              displayEmpty
              sx={styles.selectField}
              value={roleFilter}
              onChange={(e): void => {
                const value = e.target.value;
                setRoleFilter(value === '' ? '' : Number(value));
              }}
              size="small"
              renderValue={(selected: number | ''): ReactElement => {
                if (selected === '') {
                  return <span style={{ color: gray60 }}>権限を選択</span>;
                }
                if (selected === userRole.companyAdmin) {
                  return <span>{USER_ROLE_COMPANY_ADMIN}</span>;
                }
                return <span>{USER_ROLE_COMPANY_MEMBER}</span>;
              }}
            >
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value={userRole.companyAdmin}>{USER_ROLE_COMPANY_ADMIN}</MenuItem>
              <MenuItem value={userRole.companyMember}>{USER_ROLE_COMPANY_MEMBER}</MenuItem>
            </Select>
            <Button
              onClick={(): void => {
                setSearchText('');
                setRoleFilter('');
              }}
              sx={styles.clearButton}
            >
              <Typography sx={{ fontSize: '14px' }}>検索クリア</Typography>
            </Button>
          </Box>
        </Box>
        <Box sx={styles.mainContainer}>
          <StaffListCard
            isAdmin={isUserAdmin}
            staffList={filteredUsers}
            loginUser={loginUser}
            groupNameMap={groupNameMap}
          />
        </Box>
      </Box>
      {isUserAdmin ? (
        <Box sx={[styles.mobileInviteButtonSpace, styles.mobileStaffInviteButton]}>
          <InviteButton
            forPc={false}
            registeredUsers={registeredUsers}
            userLimit={userLimit}
            onLimitReached={handleLimitReached}
          />
        </Box>
      ) : null}
      <AccountManagementDialog
        open={limitDialogOpen}
        onClose={(): void => setLimitDialogOpen(false)}
        onContact={(): void => {
          setLimitDialogOpen(false);
          navigate(
            companyPlan === companyPlanEnum.free
              ? '/contact?category=plan&type=plan_change'
              : '/contact?category=plan&type=additional_user',
          );
        }}
        title="担当者数の上限に達しました"
        descriptionLines={
          companyPlan === companyPlanEnum.free
            ? [
                'フリープランでは担当者は1名までとなっています。',
                '複数名でご利用いただく場合は、スタンダードプラン以上へのアップグレードをご検討ください。',
              ]
            : [
                `担当者数が上限（${userLimit}名）に達しています。`,
                '担当者の追加をご希望の場合は、お問い合わせフォームより追加希望人数をお知らせください。',
              ]
        }
        contactButtonText={
          companyPlan === companyPlanEnum.free ? 'プランの変更を申し込む' : '担当者の追加を申し込む'
        }
        hideCancelButton={true}
      />
      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarMessage}
      />
    </Box>
  );
};

export default Staff;
